import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { getAdminSession, cmsFetch } from "@jambushrushti/services/adminAuth";

export interface Guruvani {
  id: string;
  title: string;
  content: string | null;
  category: string | null;
  source: string | null;
  tags: string;
  status: string;
  is_published: boolean;
  is_restricted: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface GuruvaniInput {
  title?: string;
  content?: string;
  category?: string;
  source?: string;
  tags?: string;
  status?: string;
  is_published?: boolean;
  is_restricted?: boolean;
}

type ApiRecord = Record<string, unknown>;

const baseUrl = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const tenantId =
  import.meta.env.VITE_X_TENANTID || "6cf833d7-bf6f-4688-9136-5674d8053b85";
const endpoint = `${baseUrl}/jambushrusti/cms/guruvani/`;

// Headers managed by cmsFetch

async function parseResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.message ||
      payload?.error?.message ||
      payload?.error;
    throw new Error(
      typeof message === "string" ? message : "Guruvani request failed."
    );
  }
  return payload;
}

function normalize(record: ApiRecord): Guruvani {
  const status = String(
    record.status || (record.is_published ? "Published" : "Draft")
  );
  return {
    id: String(record.id || record.uuid || ""),
    title: String(record.title || ""),
    content: record.content == null ? null : String(record.content),
    category: record.category == null ? null : String(record.category),
    source: record.source == null ? null : String(record.source),
    tags: Array.isArray(record.tags)
      ? record.tags.join(", ")
      : String(record.tags || ""),
    status,
    is_published:
      status.toLowerCase() === "published" || record.is_published === true,
    is_restricted:
      record.is_restricted_access === true || record.is_restricted === true,
    created_at:
      record.created_at == null ? undefined : String(record.created_at),
    updated_at:
      record.updated_at == null ? undefined : String(record.updated_at),
  };
}

function toApiPayload(input: GuruvaniInput) {
  const published =
    input.is_published ?? input.status?.toLowerCase() === "published";
  return {
    ...(input.title !== undefined ? { title: input.title } : {}),
    ...(input.category !== undefined ? { category: input.category } : {}),
    ...(input.source !== undefined ? { source: input.source } : {}),
    ...(input.content !== undefined ? { content: input.content } : {}),
    ...(input.tags !== undefined ? { tags: input.tags } : {}),
    ...(input.status !== undefined || input.is_published !== undefined
      ? { status: published ? "Published" : "Draft" }
      : {}),
    ...(input.is_restricted !== undefined
      ? { is_restricted_access: input.is_restricted }
      : {}),
  };
}

export function useGuruvaniList(_includeUnpublished = false) {
  return useQuery({
    queryKey: ["guruvani"],
    queryFn: async () => {
      const response = await cmsFetch(endpoint, { method: "GET" });
      const payload = await parseResponse(response);
      const rows =
        payload?.results || payload?.data?.results || payload?.data || payload;
      return (Array.isArray(rows) ? rows : []).map(row => normalize(row));
    },
  });
}

export function useGuruvaniById(id: string) {
  return useQuery({
    queryKey: ["guruvani", id],
    queryFn: async () => {
      const response = await cmsFetch(`${endpoint}${id}/`);
      const payload = await parseResponse(response);
      return normalize(payload?.data || payload);
    },
    enabled: Boolean(id),
  });
}

function useGuruvaniMutation<T>(
  mutationFn: (value: T) => Promise<unknown>,
  successMessage: string
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guruvani"] });
      toast({ title: "Success", description: successMessage });
    },
    onError: (error: Error) =>
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      }),
  });
}

export function useCreateGuruvani() {
  return useGuruvaniMutation(async (input: GuruvaniInput) => {
    const response = await cmsFetch(endpoint, {
      method: "POST",
      body: JSON.stringify(toApiPayload(input)),
    });
    return parseResponse(response);
  }, "Guruvani created successfully");
}

export function useUpdateGuruvani() {
  return useGuruvaniMutation(
    async ({ id, ...input }: GuruvaniInput & { id: string }) => {
      const response = await cmsFetch(`${endpoint}${id}/`, {
        method: "PATCH",
        body: JSON.stringify(toApiPayload(input)),
      });
      return parseResponse(response);
    },
    "Guruvani updated successfully"
  );
}

export function useDeleteGuruvani() {
  return useGuruvaniMutation(async (id: string) => {
    const response = await cmsFetch(`${endpoint}${id}/`, {
      method: "DELETE",
    });
    return parseResponse(response);
  }, "Guruvani deleted successfully");
}
